import { ProcessingLevel } from "@redotech/crdb-enums/fulfillment/processing-level";
import { InventoryItemOutcome } from "@redotech/crdb-enums/inventory/inventory-item-outcome";
import { InventoryRuleStatus } from "@redotech/crdb-enums/inventory/inventory-rule-status";
import { ReturnVerificationResult } from "@redotech/crdb-enums/returns/return-verification-result";
import {
  inventoryRuleConditionsSchema,
  inventoryRuleFactSchema,
} from "@redotech/redo-inventory-common/inventory-rules/conditions";
import {
  inventoryRuleActionSchema,
  inventoryRuleTriggerSchema,
} from "@redotech/redo-inventory-common/inventory-rules/rule-definition";
import { INVENTORY_RULE_BRANCH_RESULTS } from "@redotech/redo-inventory-common/inventory-rules/rule-scope";
import {
  inventoryRuleIdFormat,
  inventoryRuleVersionIdFormat,
  locationIdFormat,
} from "@redotech/redo-inventory-id";
import * as z from "zod";

/**
 * For items a change newly reaches that are already past the step's day: `next_run` leaves them
 * to the next midnight run, `skip` records a skipped decision so the step never acts on them.
 */
export const inventoryRuleExistingItemsSchema = z.enum(["next_run", "skip"]);
export type InventoryRuleExistingItems = z.infer<
  typeof inventoryRuleExistingItemsSchema
>;

/** A rule as the editor writes it, for one warehouse. */
export const inventoryRuleDraftSchema = z.object({
  trigger: inventoryRuleTriggerSchema,
  /**
   * Without the conditions the API adds: the warehouse, `location_kind is
   * returns_center` and override exclusions. A negated group is refused.
   */
  criteria: inventoryRuleConditionsSchema,
  action: inventoryRuleActionSchema,
});
export type InventoryRuleDraft = z.infer<typeof inventoryRuleDraftSchema>;

export const inventoryRuleVersionSchema = z.object({
  id: inventoryRuleVersionIdFormat.schema,
  version: z.int().positive(),
  trigger: inventoryRuleTriggerSchema,
  /** As stored, with every condition the API added. */
  conditions: inventoryRuleConditionsSchema,
  action: inventoryRuleActionSchema,
  createdAt: z.date(),
});

export const inventoryRuleSchema = z.object({
  id: inventoryRuleIdFormat.schema,
  status: z.enum(InventoryRuleStatus),
  /** Run order among the merchant's live rules with the same trigger. */
  position: z.int(),
  locationId: locationIdFormat.schema,
  /** The active version's conditions as the editor saved them. */
  criteria: inventoryRuleConditionsSchema,
  /** Criteria of higher-ranked overrides that the API excludes from this rule. */
  exclusions: z.array(inventoryRuleConditionsSchema),
  activeVersion: inventoryRuleVersionSchema,
  updatedAt: z.date(),
});
export type InventoryRuleView = z.infer<typeof inventoryRuleSchema>;

/**
 * Which items a lane covers, at verification or grading; receiving runs no rules. Fixed rows: `grade` at grading,
 * `verification` at verification, `additional_item` and `backstop` at both. Overrides rank above them.
 */
export const inventoryLaneRowSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("grade"), gradeName: z.string().trim().min(1) }),
  z.object({
    type: z.literal("verification"),
    result: z.enum([
      ReturnVerificationResult.Pass,
      ReturnVerificationResult.Fail,
    ]),
  }),
  /** Undeclared items with (`identified`) or without a product; takes one day 0 outcome. */
  z.object({ type: z.literal("additional_item"), identified: z.boolean() }),
  /** One scheduled outcome for items with no outcome yet: no item conditions, ranked last, never excluded. */
  z.object({ type: z.literal("backstop") }),
  z.object({
    type: z.literal("override"),
    /** A negated group is refused, as for rule criteria. */
    criteria: inventoryRuleConditionsSchema,
  }),
]);
export type InventoryLaneRow = z.infer<typeof inventoryLaneRowSchema>;

const laneDaySchema = z.int().min(0).max(3650);

/** An action of a step; `when` runs it only if an earlier action of the step applied or didn't. */
export const inventoryLaneStepActionSchema = z.object({
  action: inventoryRuleActionSchema,
  when: z
    .object({
      /** An earlier action of the same step. */
      actionIndex: z.int().min(0),
      result: z.enum(INVENTORY_RULE_BRANCH_RESULTS),
    })
    .optional(),
});

/**
 * Day 0 runs when the item is processed; day N once it has been N days in a bin.
 * Each action is its own rule, run in list order; at most one outcome per path through it.
 */
export const inventoryLaneStepDraftSchema = z.object({
  day: laneDaySchema,
  actions: z.array(inventoryLaneStepActionSchema).min(1),
  /** Defaults to `next_run`. */
  existingItems: inventoryRuleExistingItemsSchema.optional(),
});

export const inventoryLaneDraftSchema = z.object({
  locationId: locationIdFormat.schema,
  processingLevel: z.enum(ProcessingLevel),
  /** The lane being replaced; unset adds one. */
  laneKey: z.string().min(1).optional(),
  row: inventoryLaneRowSchema,
  /** Days strictly increasing. An empty list clears the row's rules. */
  steps: z.array(inventoryLaneStepDraftSchema),
  /** A new override's rank among the overrides, 0 first; unset ranks it first. */
  rank: z.int().min(0).optional(),
});
export type InventoryLaneDraft = z.infer<typeof inventoryLaneDraftSchema>;

export const inventoryLaneSchema = z.object({
  /** Stable while the row's criteria are unchanged; unique per warehouse and level. */
  laneKey: z.string(),
  row: inventoryLaneRowSchema,
  /** Overrides only, 0 first. */
  rank: z.int().min(0).nullable(),
  /** A grade row for a grade the merchant no longer offers, kept while it has live rules. */
  legacy: z.boolean(),
  steps: z.array(
    z.object({
      day: laneDaySchema,
      /** In run order, without the restock rule added before a transform or listing action. */
      actions: z.array(inventoryLaneStepActionSchema),
      /** Each action's rule, in the same order. */
      ruleIds: z.array(inventoryRuleIdFormat.schema),
    }),
  ),
});
export type InventoryLaneView = z.infer<typeof inventoryLaneSchema>;

/** An unsaved change to preview or analyze: one rule, or one lane. */
export const inventoryRuleChangeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("rule"),
    locationId: locationIdFormat.schema,
    /** The rule being edited; unset adds one. */
    ruleId: inventoryRuleIdFormat.schema.optional(),
    rule: inventoryRuleDraftSchema,
  }),
  z.object({ kind: z.literal("lane"), lane: inventoryLaneDraftSchema }),
]);
export type InventoryRuleChange = z.infer<typeof inventoryRuleChangeSchema>;

export const inventoryRuleSaveResultSchema = z.object({
  /** Where a saved scheduled rule's operator doesn't strictly scan the merchant, so the daily job skips it. */
  locationsWithoutStrictScan: z.array(locationIdFormat.schema),
  /** Groups the skipped rows a `skip` choice writes in the background; null when nothing is skipped. */
  skipRunId: z.string().nullable(),
});

export const inventoryRuleSettingsLocationSchema = z.object({
  locationId: locationIdFormat.schema,
  processingLevel: z.enum(ProcessingLevel),
  supportedOutcomes: z.array(z.enum(InventoryItemOutcome)),
});

const coverageValueSchema = z.union([
  z.boolean(),
  z.number(),
  z.string(),
  z.null(),
  z.array(z.string()),
  z.object({ anythingElse: z.literal(true) }),
]);

/**
 * The coverage analyzer's result. Rule ids are saved ids, or for unsaved lane rules
 * `draft:<step index>:<action index>` and `draft:<step index>:restock`.
 */
export const inventoryRuleCoverageSchema = z.object({
  locations: z.array(
    z.object({
      locationId: locationIdFormat.schema,
      groups: z.array(
        z.object({
          id: z.int(),
          facts: z.partialRecord(inventoryRuleFactSchema, coverageValueSchema),
          arrivedFromLocationId: locationIdFormat.schema.nullable(),
        }),
      ),
      steps: z.array(
        z.object({
          ruleId: z.string(),
          matchedGroupIds: z.array(z.int()),
          remainingGroupIds: z.array(z.int()),
        }),
      ),
      unterminated: z.array(
        z.object({
          groupId: z.int(),
          appliedRuleIds: z.array(z.string()),
          outcome: z.enum(InventoryItemOutcome).nullable(),
        }),
      ),
      truncated: z.boolean(),
    }),
  ),
  rules: z.array(
    z.object({
      ruleId: z.string(),
      reachableLocationIds: z.array(locationIdFormat.schema),
      unreachable: z
        .discriminatedUnion("reason", [
          z.object({ reason: z.literal("no_matching_items") }),
          z.object({ reason: z.literal("anchor_never_applied") }),
          z.object({
            reason: z.literal("removed_by_earlier_rule"),
            ruleId: z.string(),
          }),
        ])
        .nullable(),
    }),
  ),
  unsupportedOutcomes: z.array(
    z.object({
      ruleId: z.string(),
      locationId: locationIdFormat.schema,
      outcome: z.enum(InventoryItemOutcome),
    }),
  ),
});
